import React, { useState, useRef } from 'react';
import { uploadImage, validateImageFile } from '../services/storageService';
import { handleImageError } from '../utils/imageUtils';

interface ImageUploadProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  maxImages?: number;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ 
  images, 
  onImagesChange, 
  maxImages = 5 
}) => {
  const [urlInput, setUrlInput] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddUrl = () => {
    if (urlInput.trim() && images.length < maxImages) {
      const newImages = [...images, urlInput.trim()];
      onImagesChange(newImages);
      setUrlInput('');
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files) return;

    setError('');
    setUploading(true);

    try {
      const filesToUpload = Array.from(files).slice(0, maxImages - images.length);
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      
      // Validate all files first
      for (const file of filesToUpload) {
        const validation = validateImageFile(file);
        if (validation.isValid) {
          validFiles.push(file);
        } else {
          invalidFiles.push(`${file.name}: ${validation.error}`);
        }
      }

      // Show validation errors if any
      if (invalidFiles.length > 0) {
        setError(`Some files were skipped:\n${invalidFiles.join('\n')}`);
      }

      // Upload valid files in parallel for better performance
      if (validFiles.length > 0) {
        const uploadPromises = validFiles.map(async (file) => {
          const fileId = `${file.name}-${Date.now()}-${Math.random()}`;
          setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

          try {
            // Upload to Firebase Storage
            const result = await uploadImage(file, 'cars');
            
            // Remove from progress
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[fileId];
              return newProgress;
            });

            return result.url;
          } catch (uploadError: any) {
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[fileId];
              return newProgress;
            });
            throw new Error(`${file.name}: ${uploadError.message}`);
          }
        });

        // Wait for all uploads to complete
        const results = await Promise.allSettled(uploadPromises);
        const successfulUploads: string[] = [];
        const failedUploads: string[] = [];

        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            successfulUploads.push(result.value);
          } else {
            failedUploads.push(result.reason.message);
          }
        });

        // Add successful uploads to images
        if (successfulUploads.length > 0) {
          const newImages = [...images, ...successfulUploads];
          onImagesChange(newImages);
        }

        // Show failed upload errors
        if (failedUploads.length > 0) {
          const existingError = error ? `${error}\n\n` : '';
          setError(`${existingError}Upload failures:\n${failedUploads.join('\n')}`);
        }
      }
    } catch (error: any) {
      setError(error.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Car Images ({images.length}/{maxImages})
        </label>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-red-700 text-sm whitespace-pre-line">{error}</div>
          </div>
        )}

        {/* Upload Progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-700 text-sm mb-2">Uploading images...</p>
            {Object.entries(uploadProgress).map(([fileId, progress]) => (
              <div key={fileId} className="flex items-center space-x-2">
                <div className="flex-1 bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <span className="text-xs text-blue-600">{Math.round(progress)}%</span>
              </div>
            ))}
          </div>
        )}
        
        {/* URL Input */}
        <div className="flex space-x-2 mb-4">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Enter image URL"
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={handleAddUrl}
            disabled={!urlInput.trim() || images.length >= maxImages || uploading}
            className="px-4 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Add URL
          </button>
        </div>

        {/* File Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragOver 
              ? 'border-primary-500 bg-primary-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFileUpload(e.target.files)}
            className="hidden"
          />
          
          <div className="space-y-2">
            <svg className="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || images.length >= maxImages}
                className="text-primary-600 hover:text-primary-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Click to upload'}
              </button>
              <span className="text-gray-500"> or drag and drop</span>
            </div>
            <p className="text-sm text-gray-500">
              PNG, JPG, WebP up to 5MB each
            </p>
          </div>
        </div>
      </div>

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={image}
                  alt={`Car ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => handleImageError(e, 'error', 200, 200)}
                />
              </div>
              
              {/* Remove button */}
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Primary image indicator */}
              {index === 0 && (
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-primary-500 text-white text-xs rounded-full">
                  Primary
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      <div className="text-sm text-gray-500">
        <p>• First image will be used as the primary image</p>
        <p>• You can add images via URL or file upload</p>
        <p>• Drag and drop multiple images to upload them all at once</p>
        <p>• Supports JPEG, PNG, and WebP formats</p>
        <p>• Multiple files are uploaded in parallel for faster processing</p>
      </div>
    </div>
  );
};
